import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { Tooltip } from 'primereact/tooltip';
import FloatingProgressBar from 'components/FloatingProgressBar/FloatingProgressBar';

const DownloadTray = ({
  folderDownloads = [],
  fileDownloads = [],
  downloadProgress = {},
  onClose,
  onRemoveDownload,
  onRemoveFileDownload,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);

  // Constants for download types
  const PDF_DOWNLOAD_TYPES = ['call_logs', 'case_notes', 'secure_message', 'case_notes_call_logs'];

  // Helper function to check if download type generates PDF
  const isPdfDownloadType = (downloadType) => {
    return PDF_DOWNLOAD_TYPES.includes(downloadType);
  };

  // Helper function to get status message based on download type and status
  const getDownloadStatusMessage = (download) => {
    const { type, status } = download;

    const statusMessages = {
      'In Progress': isPdfDownloadType(type) ? 'Preparing pdf file...' : 'Preparing zip file...',
      completed: 'Downloading...',
      failed: 'Failed to download',
    };

    return statusMessages[status] || 'Unknown status';
  };

  const handleClose = () => {
    onClose();
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const hasAnyDownloads = folderDownloads?.length > 0 || fileDownloads?.length > 0 || Object.keys(downloadProgress)?.length > 0;

  if (!hasAnyDownloads) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 10000,
        minWidth: '350px',
        maxWidth: '400px',
      }}
    >
      <div
        style={{
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          maxWidth: '360px',
        }}
        className="p-0 card-bg"
      >
        <div
          className="d-flex justify-content-between align-items-center inner-border-bottom "
          style={{
            borderRadius: '8px 8px 0 0',
            padding: '12px 16px',
          }}
        >
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <span style={{ fontWeight: '600', fontSize: '14px' }}>Downloads</span>
          </div>
          <div className="d-flex" style={{ gap: '4px' }}>
            <Button
              icon={isMinimized ? 'fas fa-chevron-up' : 'fas fa-chevron-down'}
              className="p-button-text p-button-sm"
              style={{ color: 'white', padding: '4px' }}
              onClick={toggleMinimize}
              tooltip={isMinimized ? 'Expand' : 'Minimize'}
              tooltipOptions={{ position: 'top' }}
            />
            <Button
              icon="fas fa-times"
              className="p-button-text p-button-sm"
              style={{ color: 'white', padding: '4px' }}
              onClick={handleClose}
              tooltip="Close"
              tooltipOptions={{ position: 'top' }}
            />
          </div>
        </div>

        {!isMinimized && (
          <div style={{ padding: '16px', maxHeight: '400px', overflowY: 'auto' }}>
            {fileDownloads?.length > 0 && (
              <div>
                {fileDownloads.map((download) => {
                  if (download?.downloadSuccess) {
                    return (
                      <div key={download?.id} className="inner-card p-3 w-100 mb-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-1" style={{ marginRight: '8px' }}>
                            <p className="mb-2" style={{ fontWeight: '600', fontSize: '14px' }}>
                              {download?.successHeadline || 'Case files downloaded successfully'}
                            </p>
                            <p className="text-hint mb-1" style={{ fontSize: '12px', wordBreak: 'break-word' }}>
                              File: {download?.fileName}
                            </p>
                            {download?.totalFiles != null && download?.totalFiles !== '' && (
                              <p className="text-hint mb-0" style={{ fontSize: '12px' }}>
                                Total files: {download?.totalFiles}
                              </p>
                            )}
                          </div>
                          <Button
                            icon="fas fa-times"
                            className="p-button-text p-button-sm"
                            style={{ color: '#999', padding: '2px' }}
                            onClick={() => onRemoveFileDownload(download?.id)}
                            tooltip="Dismiss"
                            tooltipOptions={{ position: 'top' }}
                          />
                        </div>
                      </div>
                    );
                  }
                  if (download?.showProgressBar) {
                    return (
                      <FloatingProgressBar
                        key={download.id}
                        progress={download.progress}
                        fileName={download.fileName}
                        error={download.error}
                        id={download.id}
                        remove={onRemoveFileDownload}
                      />
                    );
                  }
                  return null;
                })}
              </div>
            )}

            {/* Background downloads in progress*/}
            {Object.keys(downloadProgress)?.length > 0 && (
              <div>
                {Object.entries(downloadProgress)?.map(([downloadId, progressData]) => {
                  const isInTray = folderDownloads?.some((download) => download.id === downloadId);
                  if (isInTray) return null;

                  return (
                    <div key={downloadId} className="inner-card p-3 w-100 mb-2">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-1" style={{ marginRight: '8px' }}>
                          <div className="d-flex align-items-center">
                            <i className="icon-folder me-2"></i>
                            <p className="mb-0">{progressData.fileName?.split('_')[0] || 'Folder'}</p>
                          </div>
                        </div>
                      </div>

                      <div style={{ marginTop: '8px' }}>
                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="text-hint text-truncate">{progressData.fileName}</span>
                            <span style={{ fontSize: '10px', color: '#666' }}>{Math.round(progressData?.progress)}%</span>
                          </div>
                          <ProgressBar value={progressData.progress} style={{ height: '6px' }} showValue={false} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {folderDownloads.length > 0 && (
              <div>
                {folderDownloads.map((download) => (
                  <div key={download.id} className="inner-card p-3 w-100 mb-2">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="flex-1" style={{ marginRight: '8px' }}>
                        <div className="d-flex  align-items-center">
                          <i className="icon-folder me-2"></i>
                          <p className="mb-0">{download.folderName}</p>
                        </div>
                        <div className="text-hint">{getDownloadStatusMessage(download)}</div>
                      </div>
                      <Button
                        icon="fas fa-times"
                        className="p-button-text p-button-sm"
                        style={{ color: '#999', padding: '2px' }}
                        onClick={() => onRemoveDownload(download.id)}
                        tooltip="Remove"
                        tooltipOptions={{ position: 'top' }}
                      />
                    </div>

                    {download.status === 'failed' && (
                      <div
                        style={{
                          color: '#dc3545',
                          fontSize: '11px',
                          padding: '8px',
                          backgroundColor: '#f8d7da',
                          borderRadius: '4px',
                          border: '1px solid #f5c6cb',
                        }}
                      >
                        {download?.type && isPdfDownloadType(download?.type)
                          ? 'Failed to prepare pdf file. Please try again.'
                          : 'Failed to prepare zip file. Please try again.'}
                      </div>
                    )}

                    {download?.zipFile && (
                      <div style={{ marginTop: '8px' }}>
                        {/* Show progress if downloading */}
                        {downloadProgress[download.id]?.isDownloading ? (
                          <div>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span className="text-hint text-truncate">{downloadProgress[download.id].fileName}</span>
                              <span style={{ fontSize: '10px', color: '#666' }}>{Math.round(downloadProgress[download.id].progress)}%</span>
                            </div>
                            <ProgressBar value={downloadProgress[download.id].progress} style={{ height: '6px' }} showValue={false} />
                            {downloadProgress[download.id].error && (
                              <div className="mt-1" style={{ fontSize: '10px', color: '#dc3545' }}>
                                {downloadProgress[download.id].error}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="d-flex justify-content-between align-items-center mb-1 inner-card p-2">
                            <p className="flex-1 mb-0 text-truncate" title={download.zipFile?.fileName}>
                              {download.zipFile?.fileName}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <Tooltip target=".p-button" />
    </div>
  );
};

export default DownloadTray;
